import { useQuery } from "@tanstack/react-query";
import { motion, useReducedMotion } from "framer-motion";
import { AlertTriangle } from "lucide-react";
import { api } from "../../api";
import { TRAINER } from "../../constants/testIds";
import { fadeUp } from "../../lib/motion";
import { GymLoader } from "../../components/common/GymLoader";

function fmtTime(iso) {
  return new Date(iso).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" });
}

// UX-TR-01 §1 SCR-P06-01: today's sessions (bundled, no N+1) + a client-slipping feed — both
// server-scoped to this trainer's own provider_ref (see `routers/trainer_app.py`), never a
// client-side filter of a wider list.
export default function Today() {
  const reducedMotion = useReducedMotion();
  const homeQ = useQuery({ queryKey: ["trainer-home"], queryFn: async () => (await api.get("/trainer/home")).data });

  if (homeQ.isLoading) return <GymLoader fullPage label="Loading your day…" testId="trainer-today-loader" />;
  const sessions = homeQ.data?.today_sessions || [];
  const slipping = homeQ.data?.slipping_clients || [];

  return (
    <div data-testid={TRAINER.todayPage} className="space-y-6 max-w-2xl">
      <motion.div {...fadeUp(reducedMotion)}>
        <h1 className="text-2xl font-bold" style={{ color: "#141416" }}>Today</h1>
        <p className="text-sm mt-0.5" style={{ color: "#68686E" }}>Your sessions for today, and clients who may need a nudge.</p>
      </motion.div>

      <motion.div {...fadeUp(reducedMotion, 0.04)} className="rounded-[14px] overflow-hidden" style={{ background: "#fff", border: "1px solid #EBEAE5" }}>
        <p className="text-sm font-semibold px-4 py-3" style={{ color: "#141416", borderBottom: "1px solid #EBEAE5" }}>Sessions today</p>
        {sessions.length === 0 && <p className="text-sm px-4 py-6" style={{ color: "#68686E" }}>No sessions scheduled for today.</p>}
        {sessions.map((s) => (
          <div key={s.id} data-testid={TRAINER.todaySessionRow(s.id)} className="flex items-center justify-between px-4 py-3" style={{ borderBottom: "1px solid #F4F3EF" }}>
            <div>
              <p className="text-sm font-medium" style={{ color: "#141416" }}>{s.member_name || "—"}</p>
              <p className="text-xs mt-0.5" style={{ color: "#68686E" }}>{fmtTime(s.slot.start)} · {s.session_kind === "pt" ? "1:1 PT" : s.class_type}</p>
            </div>
            <span className="status-pill" data-testid={`${TRAINER.todaySessionRow(s.id)}-status`}>{s.status}</span>
          </div>
        ))}
      </motion.div>

      <motion.div {...fadeUp(reducedMotion, 0.08)} className="rounded-[14px] overflow-hidden" style={{ background: "#fff", border: "1px solid #EBEAE5" }}>
        <p className="flex items-center gap-1.5 text-sm font-semibold px-4 py-3" style={{ color: "#141416", borderBottom: "1px solid #EBEAE5" }}>
          <AlertTriangle size={14} style={{ color: "var(--scarlet-neg)" }} /> Clients slipping
        </p>
        {slipping.length === 0 && <p className="text-sm px-4 py-6" style={{ color: "#68686E" }}>Nobody's flagged as slipping right now.</p>}
        {slipping.map((c) => (
          <div key={c.member_ref} data-testid={TRAINER.slippingClientRow(c.member_ref)} className="px-4 py-3" style={{ borderBottom: "1px solid #F4F3EF" }}>
            <p className="text-sm font-medium" style={{ color: "#141416" }}>{c.member_name}</p>
            <p className="text-xs mt-0.5" style={{ color: "var(--scarlet-neg)" }}>{c.reason}</p>
          </div>
        ))}
      </motion.div>
    </div>
  );
}
